<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="80%">
    <Form
      ref="formRef"
      v-loading="formLoading"
      :isCol="true"
      :rules="formRules"
      :schema="formSchema"
    >
      <template #spuId>
        <el-button @click="spuSelectRef.open()">选择商品</el-button>
        <SpuAndSkuList
          ref="spuAndSkuListRef"
          :rule-config="ruleConfig"
          :spu-list="spuList"
          :spu-property-list-p="spuPropertyList"
        >
          <el-table-column align="center" label="预售模式" min-width="120">
            <template #default="{ row: sku }">
              <el-select
                v-model="sku.productConfig.presaleMode"
                class="w-100%"
                @change="() => onModeChange(sku)"
              >
                <el-option :value="1" label="全款预售" />
                <el-option :value="2" label="定金预售" />
              </el-select>
            </template>
          </el-table-column>
          <el-table-column align="center" label="预计发货" min-width="180">
            <template #default="{ row: sku }">
              <el-date-picker
                v-model="sku.productConfig.shipTime"
                class="!w-100%"
                type="datetime"
                value-format="x"
                placeholder="发货时间"
              />
            </template>
          </el-table-column>
          <el-table-column align="center" label="活动库存" min-width="140">
            <template #default="{ row: sku }">
              <el-input-number
                v-model="sku.productConfig.stock"
                :max="sku.stock"
                :min="1"
                class="w-100%"
              />
            </template>
          </el-table-column>
          <el-table-column align="center" label="定金" min-width="200">
            <template #default="{ row: sku }">
              <template v-if="sku.productConfig.presaleMode === 2">
                <el-select v-model="sku.productConfig.depositType" class="!w-90px mb-5px">
                  <el-option :value="1" label="固定" />
                  <el-option :value="2" label="百分比" />
                </el-select>
                <el-input-number
                  v-if="sku.productConfig.depositType === 1"
                  v-model="sku.productConfig.depositValue"
                  :min="0.01"
                  :precision="2"
                  :step="1"
                  class="w-100%"
                  placeholder="元"
                />
                <el-input-number
                  v-else
                  v-model="sku.productConfig.depositValue"
                  :min="0.01"
                  :max="99.99"
                  :precision="2"
                  class="w-100%"
                  placeholder="%"
                />
              </template>
              <span v-else class="text-gray-400">—</span>
            </template>
          </el-table-column>
          <el-table-column align="center" label="尾款窗口" min-width="260">
            <template #default="{ row: sku }">
              <template v-if="sku.productConfig.presaleMode === 2">
                <el-select v-model="sku.productConfig.finalPayType" class="!w-100px mb-5px">
                  <el-option :value="1" label="绝对时间" />
                  <el-option :value="2" label="相对天数" />
                </el-select>
                <div v-if="sku.productConfig.finalPayType === 1" class="flex flex-col gap-5px">
                  <el-date-picker
                    v-model="sku.productConfig.finalPayStart"
                    class="!w-100%"
                    type="datetime"
                    value-format="x"
                    placeholder="开始"
                  />
                  <el-date-picker
                    v-model="sku.productConfig.finalPayEnd"
                    class="!w-100%"
                    type="datetime"
                    value-format="x"
                    placeholder="结束"
                  />
                </div>
                <div v-else class="flex gap-5px items-center">
                  <el-input-number
                    v-model="sku.productConfig.finalPayStartDays"
                    :min="0"
                    class="!w-90px"
                    placeholder="起"
                  />
                  <span>~</span>
                  <el-input-number
                    v-model="sku.productConfig.finalPayEndDays"
                    :min="1"
                    class="!w-90px"
                    placeholder="止"
                  />
                  <span class="text-xs">天</span>
                </div>
              </template>
              <span v-else class="text-gray-400">—</span>
            </template>
          </el-table-column>
        </SpuAndSkuList>
      </template>
    </Form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
  <SpuSelect ref="spuSelectRef" :isSelectSku="true" :merchant="props.merchant" @confirm="selectSpu" />
</template>

<script lang="ts" setup>
import { SpuAndSkuList, SpuProperty, SpuSelect } from '../../components'
import { formSchema, createEndTimeAfterStartRule, rules } from './presaleActivity.data'
import { cloneDeep } from 'lodash-es'
import * as AdminPresaleActivityApi from '@/api/mall/promotion/presale/presaleActivity'
import type { PresaleActivityVO, PresaleProductVO } from '@/api/mall/promotion/presale/presaleActivity'
import * as MerchantPromotionApi from '@/api/merchant/promotion'
import * as AdminProductSpuApi from '@/api/mall/product/spu'
import * as MerchantProductApi from '@/api/merchant/product'
import { getPropertyList, RuleConfig } from '@/views/mall/product/spu/components'
import { convertToInteger, formatToFraction } from '@/utils'
import type { SkuExtension, SpuExtension } from '@/api/mall/promotion/seckill/seckillActivity'

defineOptions({ name: 'PromotionPresaleActivityForm' })

const props = defineProps({ merchant: { type: Boolean, default: false } })

const PresaleActivityApi = computed(() =>
  props.merchant
    ? {
        getPresaleActivity: MerchantPromotionApi.getPresaleActivity,
        createPresaleActivity: MerchantPromotionApi.createPresaleActivity,
        updatePresaleActivity: MerchantPromotionApi.updatePresaleActivity
      }
    : AdminPresaleActivityApi
)
const ProductSpuApi = computed(() => (props.merchant ? MerchantProductApi : AdminProductSpuApi))

const { t } = useI18n()
const message = useMessage()

const dialogVisible = ref(false)
const dialogTitle = ref('')
const formLoading = ref(false)
const formType = ref('')
const formRef = ref()
const formRules = reactive({
  ...rules,
  endTime: [createEndTimeAfterStartRule(() => formRef.value?.formModel?.startTime)]
})

const spuSelectRef = ref()
const spuAndSkuListRef = ref()
const ruleConfig: RuleConfig[] = [
  {
    name: 'productConfig.stock',
    rule: (arg, sku) => arg >= 1 && (sku?.stock == null || arg <= sku.stock),
    message: '活动库存不能小于 1，且不能超过商品原有库存'
  },
  {
    name: 'productConfig.presaleMode',
    rule: (arg) => arg === 1 || arg === 2,
    message: '请选择预售模式'
  },
  {
    name: 'productConfig.shipTime',
    rule: (arg) => arg != null && arg !== '',
    message: '请选择预计发货时间'
  }
]
const spuList = ref<SpuExtension[]>([])
const spuPropertyList = ref<SpuProperty<SpuExtension>[]>([])

const emptyProductConfig = (spuId: number, skuId: number): PresaleProductVO => ({
  spuId,
  skuId,
  presaleMode: 1,
  shipTime: '' as any,
  stock: 1,
  depositType: 1,
  depositValue: undefined,
  finalPayType: 1,
  finalPayStart: undefined,
  finalPayEnd: undefined,
  finalPayStartDays: 0,
  finalPayEndDays: 7
})

const onModeChange = (sku: SkuExtension) => {
  const cfg = sku.productConfig as PresaleProductVO
  if (cfg.presaleMode === 1) {
    cfg.depositType = undefined
    cfg.depositValue = undefined
    cfg.finalPayType = undefined
    cfg.finalPayStart = undefined
    cfg.finalPayEnd = undefined
    cfg.finalPayStartDays = undefined
    cfg.finalPayEndDays = undefined
  } else {
    cfg.depositType = cfg.depositType || 1
    cfg.finalPayType = cfg.finalPayType || 1
    cfg.finalPayStartDays = cfg.finalPayStartDays ?? 0
    cfg.finalPayEndDays = cfg.finalPayEndDays ?? 7
  }
}

const selectSpu = (spuId: number, skuIds: number[]) => {
  formRef.value.setValues({ spuId })
  getSpuDetails(spuId, skuIds)
}

const getSpuDetails = async (
  spuId: number,
  skuIds: number[] | undefined,
  products?: PresaleProductVO[]
) => {
  const spuProperties: SpuProperty<SpuExtension>[] = []
  const res = (await ProductSpuApi.value.getSpuDetailList([spuId])) as SpuExtension[]
  if (res.length === 0) {
    message.warning('未找到关联商品，可能已删除或无查询权限')
    return
  }
  spuList.value = []
  const spu = res[0]
  const skuIdSet = typeof skuIds === 'undefined' ? null : new Set(skuIds.map((id) => Number(id)))
  const selectSkus =
    skuIdSet == null ? spu?.skus : spu?.skus?.filter((sku) => skuIdSet.has(Number(sku.id)))
  selectSkus?.forEach((sku) => {
    let config = emptyProductConfig(spu.id!, sku.id!)
    if (products) {
      const product = products.find((item) => Number(item.skuId) === Number(sku.id))
      if (product) {
        config = cloneDeep(product)
        // 定金固定金额：分 → 元；百分比：万分比 → %
        if (config.presaleMode === 2 && config.depositValue != null) {
          if (config.depositType === 1) {
            config.depositValue = Number(formatToFraction(config.depositValue)) as any
          } else if (config.depositType === 2) {
            config.depositValue = Number(config.depositValue) / 100
          }
        }
        // 时间字段：后端 LocalDateTime 字符串，DatePicker value-format=x 需时间戳
        ;(['shipTime', 'finalPayStart', 'finalPayEnd'] as const).forEach((key) => {
          const v = (config as any)[key]
          if (v != null && typeof v === 'string' && !/^\d+$/.test(v)) {
            const ts = new Date(v.replace(/-/g, '/')).getTime()
            if (!Number.isNaN(ts)) {
              ;(config as any)[key] = ts
            }
          }
        })
      }
    }
    sku.productConfig = config
  })
  if (!selectSkus?.length) {
    message.warning('活动关联的 SKU 已不存在，请重新选择商品')
  }
  spu.skus = selectSkus as SkuExtension[]
  spuProperties.push({
    spuId: spu.id!,
    spuDetail: spu,
    propertyList: getPropertyList(spu)
  })
  spuList.value.push(spu)
  spuPropertyList.value = spuProperties
}

const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  await nextTick()
  await resetForm()
  if (id) {
    formLoading.value = true
    try {
      const data = (await PresaleActivityApi.value.getPresaleActivity(id)) as PresaleActivityVO
      formRef.value?.setValues({ ...data })
      try {
        await getSpuDetails(
          data.spuId!,
          data.products?.map((sku) => sku.skuId),
          data.products
        )
      } catch (e) {
        console.error(e)
        message.error('预售商品明细加载失败，请确认商品仍存在')
      }
    } finally {
      formLoading.value = false
    }
  }
}
defineExpose({ open })

const emit = defineEmits(['success'])
const submitForm = async () => {
  if (!formRef.value) return
  try {
    await formRef.value.getElFormRef().validate()
  } catch {
    return
  }
  const data = cloneDeep(formRef.value.formModel) as PresaleActivityVO
  if (
    data.startTime != null &&
    data.endTime != null &&
    Number(data.endTime) <= Number(data.startTime)
  ) {
    message.error('活动结束时间必须晚于开始时间')
    return
  }
  formLoading.value = true
  try {
    const products = cloneDeep(
      spuAndSkuListRef.value.getSkuConfigs('productConfig')
    ) as PresaleProductVO[]
    for (const product of products) {
      const sku = spuList.value
        .flatMap((spu) => spu.skus || [])
        .find((item) => Number(item.id) === Number(product.skuId))
      const skuStock = sku?.stock
      if (skuStock != null && Number(product.stock) > Number(skuStock)) {
        message.error(`活动库存不能超过商品原有库存（原库存 ${skuStock}）`)
        return
      }
      if (!product.shipTime) {
        message.error('请为每个 SKU 填写预计发货时间')
        return
      }
      if (product.presaleMode === 2) {
        if (!product.depositType || product.depositValue == null || Number(product.depositValue) <= 0) {
          message.error('定金预售须填写定金类型与金额')
          return
        }
        if (product.depositType === 1) {
          product.depositValue = convertToInteger(product.depositValue)
        } else {
          // % → 万分比
          product.depositValue = Math.round(Number(product.depositValue) * 100)
        }
        if (!product.finalPayType) {
          message.error('定金预售须选择尾款窗口类型')
          return
        }
        if (product.finalPayType === 1) {
          if (!product.finalPayStart || !product.finalPayEnd) {
            message.error('请填写绝对尾款时间段')
            return
          }
          if (Number(product.finalPayEnd) <= Number(product.finalPayStart)) {
            message.error('尾款结束时间须晚于开始时间')
            return
          }
        } else if (
          product.finalPayStartDays == null ||
          product.finalPayEndDays == null ||
          Number(product.finalPayEndDays) <= Number(product.finalPayStartDays)
        ) {
          message.error('相对尾款天数须满足 截止 > 起始 ≥ 0')
          return
        }
      } else {
        product.depositType = undefined
        product.depositValue = undefined
        product.finalPayType = undefined
        product.finalPayStart = undefined
        product.finalPayEnd = undefined
        product.finalPayStartDays = undefined
        product.finalPayEndDays = undefined
      }
      product.spuId = data.spuId!
    }
    data.products = products
    if (formType.value === 'create') {
      await PresaleActivityApi.value.createPresaleActivity(data)
      message.success(t('common.createSuccess'))
    } else {
      await PresaleActivityApi.value.updatePresaleActivity(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    emit('success')
  } catch {
    // validateSku / API 错误已提示
  } finally {
    formLoading.value = false
  }
}

const resetForm = async () => {
  spuList.value = []
  spuPropertyList.value = []
  await nextTick()
  formRef.value?.getElFormRef()?.resetFields()
}
</script>
